import { Link } from "react-router-dom";
import { SITE } from "../config";
import { usePageMeta } from "../seo";
import {
  ArrowRightIcon,
  BagIcon,
  BookIcon,
  CodeIcon,
  InboxIcon,
  PlugIcon,
  RouteIcon,
  MinusIcon,
} from "../components/Icons";
import { GridPattern } from "../components/Patterns";

// TODO(content): have Zopping's support lead confirm this list matches how
// the team actually works before launch.
const uses = [
  {
    Icon: InboxIcon,
    title: "One inbox for merchant questions",
    body: "Merchants who run their stores on Zopping write in, and each conversation becomes a ticket the team can assign, tag and prioritize.",
  },
  {
    Icon: BagIcon,
    title: "The merchant's store next to the ticket",
    body: "The Zopping store sidebar shows the store profile and recent orders, so an agent can see what the merchant is asking about without opening the admin.",
  },
  {
    Icon: CodeIcon,
    title: "Account details from Zopping's own systems",
    body: "The customer data lookup brings in details about the merchant's account from Zopping's systems, and the merchant's ids are attached to their customer record through the API.",
  },
  {
    Icon: PlugIcon,
    title: "Tickets from other tools",
    body: "Internal tools create and read tickets through the API with scoped keys, so a request raised elsewhere lands in the same queue.",
  },
  {
    Icon: RouteIcon,
    title: "Rules and SLA targets",
    body: "Rules assign and tag new tickets as they arrive, and SLA targets by priority show what's due next.",
  },
  {
    Icon: BookIcon,
    title: "Saved replies",
    body: "The answers the team gives every day are saved replies, with the merchant's name and the ticket subject filled in.",
  },
];

const notYet = [
  "Other customers' stories. ZopDesk is in beta and we won't invent any.",
  "Usage numbers or results. We'll publish them when we have ones worth sharing.",
];

export default function Customers() {
  usePageMeta("/customers");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-12 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            In use
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl md:text-6xl"
            style={{ animationDelay: "60ms" }}
          >
            Built with Zopping's <br className="hidden sm:block" />
            support team.
          </h1>
          <p
            className="mt-6 max-w-3xl animate-fade-up text-lg text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            {SITE.productName} is made by the team behind Zopping, and
            Zopping's own support team answers merchants from it every day.
            Much of what's in the product today exists because they needed it.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
            What they use it for
          </p>
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            A support team for an e-commerce platform.
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {uses.map(({ Icon, title, body }, i) => (
            <div
              key={title}
              className="animate-fade-up rounded-xl border border-ink-200 bg-white p-6 shadow-sm"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md shadow-brand-500/20">
                <Icon width={20} height={20} />
              </div>
              <h3 className="mb-2 text-base font-semibold text-ink-900">{title}</h3>
              <p className="text-sm leading-relaxed text-ink-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-ink-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
          <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            What you won't find here.
          </h2>
          <ul className="mt-6 space-y-3">
            {notYet.map((t) => (
              <li key={t} className="flex items-start gap-3 text-ink-700">
                <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-ink-100 text-ink-500">
                  <MinusIcon width={12} height={12} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-ink-800 bg-ink-900 p-10 text-center shadow-xl sm:p-14">
          <div className="pointer-events-none absolute -top-32 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-brand-500/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Try it with your own team.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-ink-300">
              Free during the beta. Sign in with Google, connect a channel, and
              tell us what's missing.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={SITE.appUrl}
                className="group inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400 hover:shadow-xl hover:shadow-brand-500/40"
              >
                Start free
                <ArrowRightIcon
                  width={16}
                  height={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
              <Link
                to="/contact"
                className="rounded-md border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-ink-100 backdrop-blur transition hover:border-white/25 hover:bg-white/10"
              >
                Talk to us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
